/*
 * Module for the controls on the response viewer in the topic page
 */

define([
  "marionette",
  "backbone",
  "jquery",
  "messaging/messenger",
  "modules/topicsSplit/topic/topicResponse/topicResponseCampaignModal/topicResponseCampaignModalModule",
  "modules/topicsSplit/topic/topicResponse/topicResponsePreview/topicResponsePreviewView",
  "modules/topicsSplit/topic/topicResponse/topicResponseModel",
  "modules/topicsSplit/topic/topicCopyResponseModal/topicCopyResponseModalModule",
  "modules/topicsSplit/topic/topicResponseDeleteCollabDataModal/topicResponseDeleteCollabDataModalModule",
  "modules/topicsSplit/topic/topicResponseEditorModal/topicResponseEditorModalModule",
  "modules/topicsSplit/topic/topicResponseSourceModal/topicResponseSourceModalModule",
  "modules/topicsSplit/topic/topicResponseHistoryModal/topicResponseHistoryModalModule",
  "modules/shared/actionGroupMappingModal/actionGroupMappingModalModule",
  "modules/reviewer/reviewerTranslationModal/reviewerTranslationModalModule",
  "modules/topicsSplit/topic/responseTranslationModal/responseTranslationModalModule",
  "modules/header/kbMenu/kbMenuModel",
  "modules/authoringAdmin/campaigns/campaign/campaignModel",
  "modules/topicsSplit/topic/responseTemplateStatus/responseTemplateStatusModule",
  "modules/topicsSplit/topic/shareResponseLinkModal/shareResponseLinkModalModule",
  "modules/topicsSplit/topic/topicResponse/topicResponseModel",
  "modules/topicsSplit/snippet/snippetModel",
], function (
  Marionette,
  Backbone,
  $,
  Messenger,
  TopicResponseCampaignModalModule,
  TopicResponsePreviewView,
  TopicResponseModel,
  TopicCopyResponseModalModule,
  TopicResponseDeleteCollabDataModalModule,
  TopicResponseEditorModalModule,
  TopicResponseSourceModalModule,
  TopicResponseHistoryModalModule,
  ActionGroupMappingModalModule,
  ReviewerTranslationModalModule,
  ResponseTranslationModalModule,
  KbMenuModel,
  CampaignModel,
  ResponseTemplateStatusModule,
  ShareResponseLinkModalModule,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function ({
      allowDisable,
      region,
      skipExpandWithContext,
      skipShareResponseLinks,
      skipResponseTransform,
    }) {
      this.region = region;
      this.name = "topicResponsePreview";
      this.conversationNode = null; //this variable stores the currently selected conversation node
      this.liveConversationNode = null; //stores the currently selected live conversation node, if applicable
      this.skipExpandWithContext = skipExpandWithContext;
      this.skipShareResponseLinks = skipShareResponseLinks;
      this.skipResponseTransform = skipResponseTransform;
      if (allowDisable === undefined) {
        this.allowDisable = true;
      } else {
        this.allowDisable = allowDisable;
      }
      this._setUpVents();
      this._setUpReqres();
    },
    show: function (options) {
      const {
        conversation,
        liveConversationRoot,
        readOnly,
        translationTask,
        startTouchPoint,
        startLanguage,
        targetLanguage,
        canEdit,
        showCampaigns,
      } = options;
      this.liveConversationRoot = liveConversationRoot;
      this.conversationNode = conversation;
      // store the root conversation so that we can re-fetch entire conversation before editing the
      // response. Cannot use passed conversation to fetch, as it could be a branching conversation
      // node which does not have a url set.
      // In case of branching conversation setting up root conversation
      const topic = Messenger.reqres.request("current:topic");
      if (typeof topic.conversation === "function") {
        this.rootConversation = topic.conversation();
      } else {
        this.rootConversation = conversation;
      }
      if (showCampaigns) {
        this.kbCampaignsWithDefault = Messenger.reqres.request("kb:campaignsWithDefault");
      }
      this.languages = Messenger.reqres.request("kb:languages");
      this.statuses = Messenger.reqres.request("kb:statuses");
      this.translationTask = translationTask;
      this.touchpoints = Messenger.reqres.request("kb:touchpoints");
      this.readOnly = readOnly;
      this.isAdmin = Messenger.reqres.request("user:is:admin");
      this.showCampaigns = showCampaigns;
      this.showResponseTranslation = true;
      //kb model for checking response versioning and site maps
      const kbModel = Messenger.reqres.request("active:kb:model");

      const isAuthor = Messenger.reqres.request("user:is:author");
      let selectedTouchpoint = startTouchPoint
        ? startTouchPoint
        : Messenger.reqres.request("default:touchpoint");
      this.selectedTouchpoint = selectedTouchpoint;
      let selectedLanguage = startLanguage
        ? startLanguage
        : Messenger.reqres.request("default:response:language");
      this.selectedLanguage = selectedLanguage;
      const canReviewResponse = Messenger.reqres.request("can:review:response");

      // map of response template ID to last reviewed date. When updating last reviewed date, update this map instead
      // of directly updating response template attribute to avoid triggering a conversation update server call.
      const model = new Backbone.Model({
        touchpoints: this.touchpoints,
        languages: this.languages,
        selectedTouchpoint: selectedTouchpoint, //set selected touchpoint to the default
        selectedLanguage: selectedLanguage,
        targetLanguage: targetLanguage,
        nodeToUse: conversation, //either the pending or live conversation node
        liveConversation: liveConversationRoot, //used to check whether we need to show the live response
        kbModel: kbModel,
        showPublishedResponse: false,
        response: "",
        previewResponse: "",
        readOnly: this._calculateReadOnly(this.readOnly, selectedTouchpoint, selectedLanguage),
        translationTask: translationTask,
        actionGroup: null, //define once the conversation is loaded
        touchpointStatuses: null,
        languageStatuses: null,
        responseTemplate: null,
        shareUrl: "",
        canReviewResponse: canReviewResponse,
        canEdit: !!canEdit,
        showDeleteCollabDataButton: this.isAdmin, // default to true if user is admin
        isResponseEmpty: false,
        campaign: "",
        showCampaigns: showCampaigns,
        showShareResponseLink: !this.skipShareResponseLinks,
        showResponseTranslation: this.showResponseTranslation,
      });

      this.model = model;

      const promises = [
        this.languages.onSyncedPromise(),
        this.statuses.onSyncedPromise(),
        this.touchpoints.onSyncedPromise(),
        model.get("kbModel").onSyncedPromise(),
      ];

      this.view = new TopicResponsePreviewView.topicResponsePreviewView({ model: model });

      liveConversationRoot.on(
        "showConversation",
        function () {
          //reset showPublishedResponse to false
          this.view.model.set("showPublishedResponse", false);
          this.setResponse();
          this.setActionGroup();
          this.setCampaigns();
          this.view.render();
        },
        this,
      );

      this.view.on("change:touchpoint", this._changeTouchpoint, this);

      this.view.on("change:language", this._changeLanguage, this);

      this.view.on(
        "change:targetLanguage",
        function (option) {
          this.view.model.set("targetLanguage", parseInt(option, 10));
        },
        this,
      );

      this.view.on(
        "change:response:type",
        function (option) {
          const showPublishedResponse = option === "1"; //value "1" means to show published response
          this.view.model.set("showPublishedResponse", showPublishedResponse);
          this.setResponse();
          //notify the preview module, so it can display the associated content of either the pending or published
          // response
          Messenger.vent.trigger(
            "response:type:changed",
            showPublishedResponse,
            this.getConversationNode(),
          );
        },
        this,
      );

      this.view.on("edit:response", this._editResponse, this);
      this.view.on("open:response:source", this._openResponseSource, this);
      this.view.on("delete:collab:data", this._deleteCollabData, this);
      this.view.on("open:shareResponseLink", this._openShareResponseLink, this);

      this.view.on(
        "translate:response",
        function () {
          const topicId = Messenger.reqres.request("current:topic:id");
          //open up the response translation modal and pass it the response
          Messenger.vent.trigger(
            "show:modal",
            ReviewerTranslationModalModule,
            {},
            {
              node: this.conversationNode,
              selectedTouchPoint: model.get("selectedTouchpoint"),
              selectedLanguage: model.get("selectedLanguage"),
              targetLanguage: model.get("targetLanguage"),
            },
            {
              title: "task.responseTranslator.title",
              closeButton: true,
              closeOnBackgroundClick: false,
              maximizeButton: true,
              modalHeight: "690px",
              modalWidth: "1500px",
              trackInteraction: {
                action: "Translate Response",
                id: [
                  topicId,
                  model.get("selectedTouchpoint"),
                  model.get("selectedLanguage"),
                  model.get("targetLanguage"),
                ],
                idType: ["Topic", "TouchPoint", "Source Locale", "Target Locale"],
                object: "Topic Response",
              },
            },
          );
        },
        this,
      );

      this.view.on("response:copy", this._onResponseCopy, this);

      this.view.on("response:history", this._openResponseHistory, this);

      this.view.on(
        "response:review",
        function () {
          const reviewedTimestampModel = this.conversationNode.markResponseAsReviewed(
            model.get("selectedTouchpoint"),
            model.get("selectedLanguage"),
          );
          const responseTemplate = this.view.model.get("responseTemplate");
          const templateId = responseTemplate.get("templateId");
          Messenger.vent.trigger("track:interaction", {
            action: "Review Response",
            id: templateId,
            idType: "Response Template",
            object: "Response Template",
          });
          reviewedTimestampModel.onSynced(() => {
            const oldDirty = responseTemplate.dirty;
            responseTemplate.set(
              "reviewedTimestamp",
              reviewedTimestampModel.get("lastReviewedDate"),
              { silent: true },
            );
            responseTemplate.set(
              "reviewedUserName",
              reviewedTimestampModel.get("lastReviewedUserName"),
              { silent: true },
            );
            responseTemplate.dirty = oldDirty;
            this.view.render();
          }, this);
        },
        this,
      );

      this.view.on(
        "click:topicLink",
        function (intentId) {
          if (isAuthor) {
            Messenger.save(function (success) {
              if (success) {
                const topic = Messenger.reqres.request("topic", intentId);
                Messenger.vent.trigger("show:topic", topic);
              }
            }, this);
          }
        },
        this,
      );

      //action group event handlers
      this.view.on(
        "click:action:group",
        function () {
          //make sure that the link isn't read only
          if (!this.readOnly) {
            Messenger.save(function (success) {
              if (success) {
                Messenger.vent.trigger(
                  "show:action:group",
                  this.conversationNode.getActionGroup(
                    model.get("selectedTouchpoint"),
                    model.get("selectedLanguage"),
                  ),
                );
              }
            }, this);
          }
        },
        this,
      );

      this.view.on(
        "edit:action:group",
        function () {
          const topicId = Messenger.reqres.request("current:topic:id");
          //open up action group mapping modal
          Messenger.vent.trigger(
            "show:modal",
            ActionGroupMappingModalModule,
            {
              selectedTouchPoint: model.get("selectedTouchpoint"),
              selectedLanguage: model.get("selectedLanguage"),
            },
            this.conversationNode,
            {
              title: "topic.response.actionGroup",
              closeButton: true,
              closeOnBackgroundClick: false,
              trackInteraction: {
                action: "Edit Action Group",
                id: [topicId, model.get("selectedTouchpoint"), model.get("selectedLanguage")],
                idType: ["Topic", "TouchPoint", "Locale"],
                object: "Action Group",
              },
            },
          );
        },
        this,
      );
      this.view.on(
        "delete:action:group",
        () =>
          Messenger.yesNoDialog("topic.responseProperties.removeActionGroupMessage", {
            yes: () => {
              const topicId = Messenger.reqres.request("current:topic:id");
              Messenger.vent.trigger("track:interaction", {
                action: "Delete Topic Action",
                id: [topicId, model.get("selectedTouchpoint"), model.get("selectedLanguage")],
                idType: ["Topic", "TouchPoint", "Locale"],
                object: "Topic Action",
              });
              this.conversationNode.deleteActionGroup(
                model.get("selectedTouchpoint"),
                model.get("selectedLanguage"),
              );
            },
          }),
        this,
      );

      this.view.on("open:response:translation:modal", this._openResponseTranslationModal, this);

      this.view.on("open:response:campaign:modal", this._openResponseCampaignModal, this);

      this.view.on("render", this._showSubViews, this);

      if (conversation) {
        promises.push(conversation.onSyncedPromise());
      }
      if (this.showCampaigns) {
        promises.push(this.kbCampaignsWithDefault.onSyncedPromise());
      }

      Promise.all(promises).then(() => {
        //if the module is passed a conversation object, show it
        if (conversation) {
          const responseTemplates = conversation.responseTemplates();
          responseTemplates.on("add", this._createTouchpointLocaleSets, this);
          responseTemplates.on("remove", this._createTouchpointLocaleSets, this);
          responseTemplates.on("reset", this._createTouchpointLocaleSets, this);
          this.setResponse();
          this.setActionGroup();
          this.setCampaigns();
          this._setShowResponseTranslation();
          this.showRegion(this.view);
        } else {
          this.showRegion(this.view);
        }
      });
    },
    _setShowResponseTranslation: function () {
      const languages = this.languages;
      languages.onSynced(() => {
        if (languages.length <= 1) {
          this.model.set("showResponseTranslation", false);
        } else if (
          !languages.some((lang) => !Messenger.reqres.request("kb:language:is:english", lang))
        ) {
          // none of the locales are non-english, nothing to translate to
          this.model.set("showResponseTranslation", false);
        }
      });
    },
    _openResponseCampaignModal: function () {
      if (!this.showCampaigns) {
        return;
      }
      const responseTemplate = this.conversationNode.findResponseTemplate(
        this.selectedTouchpoint,
        this.selectedLanguage,
      );
      if (!responseTemplate) {
        throw new Error("Response template not found");
      }
      const responseCampaigns = this.conversationNode.responseCampaigns();
      // find the campaign model for this response template, if there is no campaign for this
      // template, assign the default campaign
      const kbCampaignModel = this._getKbCampaignModelForTemplate(
        responseTemplate.get("templateId"),
        responseCampaigns,
      );
      Messenger.vent.trigger(
        "show:modal",
        TopicResponseCampaignModalModule,
        {},
        {
          kbCampaignModel: kbCampaignModel,
          kbCampaignsWithDefault: this.kbCampaignsWithDefault,
          conversationNode: this.conversationNode,
          responseTemplate: responseTemplate,
          responseCampaignsCollection: responseCampaigns,
        },
        {
          title: "topic.responseCampaign.title",
          closeButton: true,
          closeOnBackgroundClick: false,
          maximizeButton: false, // should be a small modal, no need to show maximize button
          modalWidth: "500px",
        },
      );
    },
    _openResponseSource: function () {
      const responseTemplate = this.conversationNode.findResponseTemplate(
        this.selectedTouchpoint,
        this.selectedLanguage,
      );
      let responseSource = responseTemplate.get("response");
      responseSource = this.conversationNode.addPrivilegedRoleNamesToResponse(responseSource);
      responseSource = TopicResponseModel.responseTemplate.prepareResponseForEdit(responseSource);
      Messenger.vent.trigger(
        "show:modal",
        TopicResponseSourceModalModule,
        {},
        {
          responseSource: responseSource,
        },
        {
          title: "topic.responseSource.title",
          closeButton: true,
          closeOnBackgroundClick: false,
          maximizeButton: true,
          modalHeight: "600px",
          modalWidth: "1000px",
        },
      );
    },
    _editResponse: function () {
      // Make sure the response template has an ID prior to editing
      let isResponseTemplateNew = false;
      const selectedTouchPoint = this.selectedTouchpoint;
      const selectedLanguage = this.selectedLanguage;
      const responseTemplate = this.conversationNode.findOrAddResponseTemplate(
        selectedTouchPoint,
        selectedLanguage,
      );
      if (responseTemplate.isNew()) {
        responseTemplate.requireSave = true;
        isResponseTemplateNew = true;
      } else {
        // Prevent the response template from being saved, because it needs to be
        // edited with the newest information that is in the database
        responseTemplate.dirty = false;
      }
      const kb = Messenger.reqres.request("active:kb:model");
      const skipCollaborationFeatures = !kb.get("responseCollaboration");
      const allowAiFeatures = kb.get("aiFeatures");
      const allowEditSource = kb.get("editSource");
      Messenger.savePromise()
        .then(() => {
          // Make sure that the absolute latest version of the conversation is always loaded
          // prior to editing.
          this.rootConversation.fetch();
          return this.rootConversation.onSyncedPromise();
        })
        .then(() => {
          //open up the response editor modal and pass it the response
          Messenger.vent.trigger(
            "show:modal",
            TopicResponseEditorModalModule,
            {},
            {
              isResponseTemplateNew: isResponseTemplateNew,
              skipCollaborationFeatures: skipCollaborationFeatures,
              allowAiFeatures: allowAiFeatures,
              skipExpandWithContext: this.skipExpandWithContext,
              skipResponseTransform: this.skipResponseTransform,
              allowEditSource: allowEditSource,
              node: this.conversationNode,
              selectedTouchPoint: selectedTouchPoint,
              selectedLanguage: selectedLanguage,
            },
            {
              title: "topic.responseEditor.title",
              closeButton: false,
              closeOnBackgroundClick: false,
              maximizeButton: true,
              modalHeight: "800px",
              modalWidth: "1000px",
              trackInteraction: {
                action: "Edit Response",
                id: [
                  Messenger.reqres.request("current:topic:id"),
                  selectedTouchPoint,
                  selectedLanguage,
                ],
                idType: ["Topic", "TouchPoint", "Locale"],
                object: "Topic Response",
              },
            },
          );
        });
    },
    _openResponseHistory: function () {
      const selectedTouchpoint = this.selectedTouchpoint;
      const selectedLanguage = this.selectedLanguage;
      const history = this.conversationNode.getResponseHistory(
        selectedTouchpoint,
        selectedLanguage,
      );
      Messenger.vent.trigger(
        "show:modal",
        TopicResponseHistoryModalModule,
        {},
        {
          conversationNode: this.conversationNode,
          selectedTouchPoint: selectedTouchpoint,
          selectedLanguage: selectedLanguage,
          historyCollection: history,
        },
        {
          title: "topic.responseHistory.title",
          closeButton: true,
          closeOnBackgroundClick: true,
          modalWidth: 1550,
          trackInteraction: {
            action: "Response History",
            id: [
              Messenger.reqres.request("current:topic:id"),
              selectedTouchpoint,
              selectedLanguage,
            ],
            idType: ["Topic", "TouchPoint", "Locale"],
            object: "Topic Response",
          },
        },
      );
    },
    _calculateReadOnly: function (readOnly, touchPointId, languageId) {
      if (readOnly) {
        return true;
      }
      // The user must be able to edit both the locale and the TouchPoint in order to edit
      // the response.
      const touchpointEditable = Messenger.reqres.request("can:edit:touchPoint", {
        touchPointId: touchPointId,
      });
      const localeEditable = Messenger.reqres.request("can:edit:locale", {
        languageId: languageId,
      });
      return !(touchpointEditable && localeEditable);
    },
    _createTouchpointLocaleSets: function () {
      if (this.translationTask) {
        return;
      }
      const model = this.view.model;
      const selectedTouchpointId = model.get("selectedTouchpoint");
      const emptyLocaleSet = new Set();
      const emptyTouchpointSet = new Set();
      const enabledLocaleSet = new Set();
      const enabledTouchpointSet = new Set();
      const publishedLocaleSet = new Set();
      const publishedTouchpointSet = new Set();
      const pendingPublishedLocaleSet = new Set();
      const pendingPublishedTouchpointSet = new Set();
      const disabledLocaleSet = new Set();
      const disabledTouchpointSet = new Set();
      const createdLocaleSet = new Set();
      const createdTouchpointSet = new Set();
      // Start with all in the empty sets
      this.touchpoints.each(function (touchpoint) {
        const touchpointId = touchpoint.get("touchPointId");
        emptyTouchpointSet.add(touchpointId);
        if (touchpointId == selectedTouchpointId) {
          this.languages.each(function (language) {
            emptyLocaleSet.add(language.get("id"));
          }, this);
        }
      }, this);
      this.conversationNode.walkAllResponseTemplates((responseTemplate) => {
        // Ignore empty responses
        if (responseTemplate.isEmpty()) {
          return;
        }
        const touchpointId = responseTemplate.get("touchpointId");
        const languageId = responseTemplate.get("languageId");
        const responseStatusId = responseTemplate.get("statusId");
        switch (responseStatusId) {
          case KbMenuModel.statuses.NON_VERSIONED_ENABLED:
            enabledTouchpointSet.add(touchpointId);
            emptyTouchpointSet.delete(touchpointId);
            if (touchpointId == selectedTouchpointId) {
              enabledLocaleSet.add(languageId);
              emptyLocaleSet.delete(languageId);
            }
            break;
          case KbMenuModel.statuses.NON_VERSIONED_CREATED:
          case KbMenuModel.statuses.VERSIONED_CREATED:
            createdTouchpointSet.add(touchpointId);
            emptyTouchpointSet.delete(touchpointId);
            if (touchpointId == selectedTouchpointId) {
              createdLocaleSet.add(languageId);
              emptyLocaleSet.delete(languageId);
            }
            break;
          case KbMenuModel.statuses.VERSIONED_DISABLED:
          case KbMenuModel.statuses.NON_VERSIONED_DISABLED:
            disabledTouchpointSet.add(touchpointId);
            emptyTouchpointSet.delete(touchpointId);
            if (touchpointId == selectedTouchpointId) {
              disabledLocaleSet.add(languageId);
              emptyLocaleSet.delete(languageId);
            }
            break;
          case KbMenuModel.statuses.VERSIONED_PUBLISHED:
            publishedTouchpointSet.add(touchpointId);
            emptyTouchpointSet.delete(touchpointId);
            if (touchpointId == selectedTouchpointId) {
              publishedLocaleSet.add(languageId);
              emptyLocaleSet.delete(languageId);
            }
            break;
          case KbMenuModel.statuses.VERSIONED_PENDING_RESPONSE:
            pendingPublishedTouchpointSet.add(touchpointId);
            emptyTouchpointSet.delete(touchpointId);
            if (touchpointId == selectedTouchpointId) {
              pendingPublishedLocaleSet.add(languageId);
              emptyLocaleSet.delete(languageId);
            }
            break;
        }
      });
      // A Touchpoint might have responses in multiple statuses based on various languages.
      // So, a there needs to be a precedence to determine what that status of the overall Touchpoint is.
      const touchpointStatuses = [];
      const createdTouchPoints = [];
      const disabledTouchPoints = [];
      const emptyTouchPoints = [];
      const enabledTouchPoints = [];
      const pendingPublishedTouchPoints = [];
      const publishedTouchPoints = [];
      this.touchpoints.each(function (touchpoint) {
        const touchPointId = touchpoint.get("touchPointId");
        if (pendingPublishedTouchpointSet.has(touchPointId)) {
          pendingPublishedTouchPoints.push(touchpoint);
        } else if (publishedTouchpointSet.has(touchPointId)) {
          publishedTouchPoints.push(touchpoint);
        } else if (enabledTouchpointSet.has(touchPointId)) {
          enabledTouchPoints.push(touchpoint);
        } else if (disabledTouchpointSet.has(touchPointId)) {
          disabledTouchPoints.push(touchpoint);
        } else if (createdTouchpointSet.has(touchPointId)) {
          createdTouchPoints.push(touchpoint);
        } else {
          emptyTouchPoints.push(touchpoint);
        }
      }, this);
      if (pendingPublishedTouchPoints.length > 0) {
        touchpointStatuses.push({
          statusName: $.t("topic.response.localePendingPublish"),
          touchpoints: pendingPublishedTouchPoints,
        });
      }
      if (publishedTouchPoints.length > 0) {
        touchpointStatuses.push({
          statusName: $.t("topic.response.localePublished"),
          touchpoints: publishedTouchPoints,
        });
      }
      if (enabledTouchPoints.length > 0) {
        touchpointStatuses.push({
          statusName: $.t("topic.response.localeEnabled"),
          touchpoints: enabledTouchPoints,
        });
      }
      if (disabledTouchPoints.length > 0) {
        touchpointStatuses.push({
          statusName: $.t("topic.response.localeDisabled"),
          touchpoints: disabledTouchPoints,
        });
      }
      if (createdTouchPoints.length > 0) {
        touchpointStatuses.push({
          statusName: $.t("topic.response.localeCreated"),
          touchpoints: createdTouchPoints,
        });
      }
      if (emptyTouchPoints.length > 0) {
        touchpointStatuses.push({
          statusName: $.t("topic.response.localeNotPublished"),
          touchpoints: emptyTouchPoints,
        });
      }
      const languageStatuses = [];
      let languages;
      if (pendingPublishedLocaleSet.size > 0) {
        languages = [];
        this.languages.each(function (language) {
          if (pendingPublishedLocaleSet.has(language.get("id"))) {
            languages.push(language);
          }
        });
        languageStatuses.push({
          statusName: $.t("topic.response.localePendingPublish"),
          languages: languages,
        });
      }
      if (publishedLocaleSet.size > 0) {
        languages = [];
        this.languages.each(function (language) {
          if (publishedLocaleSet.has(language.get("id"))) {
            languages.push(language);
          }
        });
        languageStatuses.push({
          statusName: $.t("topic.response.localePublished"),
          languages: languages,
        });
      }
      if (enabledLocaleSet.size > 0) {
        languages = [];
        this.languages.each(function (language) {
          if (enabledLocaleSet.has(language.get("id"))) {
            languages.push(language);
          }
        });
        languageStatuses.push({
          statusName: $.t("topic.response.localeEnabled"),
          languages: languages,
        });
      }
      if (disabledLocaleSet.size > 0) {
        languages = [];
        this.languages.each(function (language) {
          if (disabledLocaleSet.has(language.get("id"))) {
            languages.push(language);
          }
        });
        languageStatuses.push({
          statusName: $.t("topic.response.localeDisabled"),
          languages: languages,
        });
      }
      if (createdLocaleSet.size > 0) {
        languages = [];
        this.languages.each(function (language) {
          if (createdLocaleSet.has(language.get("id"))) {
            languages.push(language);
          }
        });
        languageStatuses.push({
          statusName: $.t("topic.response.localeCreated"),
          languages: languages,
        });
      }
      if (emptyLocaleSet.size > 0) {
        languages = [];
        this.languages.each(function (language) {
          if (emptyLocaleSet.has(language.get("id"))) {
            languages.push(language);
          }
        });
        languageStatuses.push({
          statusName: $.t("topic.response.localeNotPublished"),
          languages: languages,
        });
      }

      model.set("touchpointStatuses", touchpointStatuses);
      model.set("languageStatuses", languageStatuses);
      // render view to get changes in select view
      this.view.render();
    },

    getConversationNode: function () {
      const showPublishedResponse = this.view.model.get("showPublishedResponse");
      //turn off old 'change' event handler for nodeToUse
      this.view.model.get("nodeToUse").off("change", null, this);
      let nodeToUse = this.conversationNode;
      //if we are showing the published (live) response, then use the liveConversation to get the response
      if (showPublishedResponse) {
        //also try to select the live conversation node
        const responseId = this.conversationNode.get("responseId");
        if (responseId) {
          this.liveConversationNode = this._findMatchingLiveNode(
            this.liveConversationRoot,
            responseId,
          );
        }
        nodeToUse = this.liveConversationNode; //use the live response
      }
      return nodeToUse;
    },
    _findOrAddResponseTemplate: function (touchpointId, languageId) {
      return this.conversationNode.findOrAddResponseTemplate(touchpointId, languageId);
    },
    setResponse: function () {
      const nodeToUse = this.getConversationNode();
      this.view.model.set("nodeToUse", nodeToUse);
      //search the conversation node for the response template with the given touchpoint and language
      if (nodeToUse !== null) {
        //re-render the view when this node is changed (most likely for assoc. content changes)
        nodeToUse.on(
          "change",
          function () {
            this.view.render();
          },
          this,
        );
        const touchPointId = this.view.model.get("selectedTouchpoint");
        const languageId = this.view.model.get("selectedLanguage");
        // Always get the response template from the main conversation node, because that is where the
        // proper status is stored
        let responseTemplate = this._findOrAddResponseTemplate(touchPointId, languageId);
        this.view.model.set("responseTemplate", responseTemplate);
        this._showSubViews();
        const response = nodeToUse.getResponse(touchPointId, languageId);
        //add data-rdprivileges-name attribute to any privileged areas, so we can see the name of the roles
        //need to make sure that the privileged area roles are synced
        Messenger.reqres.request("kb:privileged:area:roles").onSynced(function () {
          const responseWithNames = nodeToUse.addPrivilegedRoleNamesToResponse(response);
          this.view.model.set("response", responseWithNames);
        }, this);
        this._previewValues();
      } else {
        this.view.model.set("response", "");
      }
    },
    _onResponseCopy: function () {
      const sourceTouchPointId = this.view.model.get("selectedTouchpoint");
      const sourceLanguageId = this.view.model.get("selectedLanguage");
      const sourceResponseTemplate = this.conversationNode.findResponseTemplate(
        sourceTouchPointId,
        sourceLanguageId,
      );
      if (!sourceResponseTemplate || sourceResponseTemplate.isEmpty()) {
        Messenger.okDialog($.t("topic.copyResponse.emptySource"));
        return;
      }
      Messenger.save((success) => {
        if (success) {
          const topicId = Messenger.reqres.request("current:topic:id");
          Messenger.vent.trigger(
            "show:modal",
            TopicCopyResponseModalModule,
            {
              namespace: this.name,
            },
            {
              conversation: this.conversationNode,
              sourceResponseTemplate: sourceResponseTemplate,
            },
            {
              title: "topic.copyResponse.title",
              closeButton: false,
              closeOnBackgroundClick: false,
              modalWidth: "550px",
              trackInteraction: {
                action: "Copy Response",
                id: [topicId, sourceTouchPointId, sourceLanguageId],
                idType: ["Topic", "TouchPoint", "Locale"],
                object: "Topic Response",
              },
            },
          );
        }
      });
    },
    _previewValues: async function () {
      const kbId = Messenger.reqres.request("active:kb");
      const allCategories = Messenger.reqres.request("kb:categories");
      const allSnippets = Messenger.reqres.request("kb:snippets", kbId);
      await Promise.all([
        this.conversationNode.onSyncedPromise(),
        allCategories.onSyncedPromise(),
        allSnippets.onSyncedPromise(),
      ]);
      this.topicCategories = this.conversationNode.categories().sort();
      this.topicAttributes = this.conversationNode.topicAttributes();
      await Promise.all([
        this.topicCategories.onSyncedPromise(),
        this.topicAttributes.onSyncedPromise(),
      ]);
      const languageId = this.view.model.get("selectedLanguage");
      let previewResponse = KbMenuModel.categories.translateCategoryForResponse(
        this.view.model.get("response"),
        this.conversationNode.get("responseId"),
        languageId,
        this.topicCategories,
        allCategories,
        true,
      );
      previewResponse = KbMenuModel.attributes.translateAttributesForResponse(
        previewResponse,
        this.conversationNode.get("responseId"),
        languageId,
        this.topicAttributes,
        true,
      );
      previewResponse = allSnippets.translateSnippetsForResponse({
        responseString: previewResponse,
        showLabel: true,
      });
      this.view.model.set("previewResponse", previewResponse);
      this._createTouchpointLocaleSets(this.view.model);
    },
    _deleteCollabData: function () {
      Messenger.savePromise().then(() => {
        Messenger.vent.trigger(
          "show:modal",
          TopicResponseDeleteCollabDataModalModule,
          {},
          {
            node: this.conversationNode,
            selectedTouchPoint: this.view.model.get("selectedTouchpoint"),
            selectedLanguage: this.view.model.get("selectedLanguage"),
          },
          {
            title: "topic.response.deleteCollabData.title",
            closeButton: true,
            closeOnBackgroundClick: false,
            maximizeButton: false,
            modalHeight: "360px",
            modalWidth: "500px",
          },
        );
      });
    },
    _setShowDeleteCollabData: function () {
      // if response template is new, there will be no collab data in database. In this
      // case, do not show the button to delete collab data. Same applies for response with empty
      // text regardless of presence of associated content.
      // Note: do not use this.conversationNode.findOrAddResponseTemplate here, because it will
      // create a new empty response template which will saved to the database by various user
      // actions such as navigating away from topic. New response should only be created when user
      // clicks on edit button.
      const responseTemplate = this.conversationNode.findResponseTemplate(
        this.selectedTouchpoint,
        this.selectedLanguage,
      );
      if (!responseTemplate || responseTemplate.isNew() || responseTemplate.isResponseTextEmpty()) {
        this.view.model.set("showDeleteCollabDataButton", false);
      } else {
        this.view.model.set("showDeleteCollabDataButton", this.isAdmin);
      }
    },
    _setIsResponseEmpty: function () {
      // The isResponseEmpty flag will be used to hide some buttons such as View Source button
      // Note: do not use this.conversationNode.findOrAddResponseTemplate here, because it will
      // create a new empty response template which will saved to the database by various user
      // actions such as navigating away from topic. New response should only be created when user
      // clicks on edit button.
      const responseTemplate = this.conversationNode.findResponseTemplate(
        this.selectedTouchpoint,
        this.selectedLanguage,
      );
      if (!responseTemplate || responseTemplate.isResponseTextEmpty()) {
        this.model.set("isResponseEmpty", true);
      } else {
        this.model.set("isResponseEmpty", false);
      }
    },
    _changeTouchpoint: function (option) {
      this.selectedTouchpoint = parseInt(option, 10);
      this.view.model.set("selectedTouchpoint", this.selectedTouchpoint);
      this.view.model.set(
        "readOnly",
        this._calculateReadOnly(this.readOnly, this.selectedTouchpoint, this.selectedLanguage),
      );
      this.setResponse();
      this.setActionGroup();
      this.setCampaigns();
      this._createTouchpointLocaleSets();
      //let other modules know that a different response template has been selected within a single response node
      Messenger.vent.trigger("selected:response:template");
    },
    _changeLanguage: function (option) {
      this.selectedLanguage = parseInt(option, 10);
      this.view.model.set("selectedLanguage", this.selectedLanguage);
      this.view.model.set(
        "readOnly",
        this._calculateReadOnly(this.readOnly, this.selectedTouchpoint, this.selectedLanguage),
      );
      this.setResponse();
      this.setActionGroup();
      this.setCampaigns();
      //let other modules know that a different response template has been selected within a single response node
      Messenger.vent.trigger("selected:response:template");
    },
    setActionGroup: function () {
      const touchPointId = this.view.model.get("selectedTouchpoint");
      const languageId = this.view.model.get("selectedLanguage");
      const actionGroup = this.conversationNode.getActionGroup(touchPointId, languageId);
      if (actionGroup) {
        //if the action group exists, wait for it to sync before setting it on the model.  This set will cause a
        // render.
        actionGroup.onSynced(function () {
          this.view.model.set("actionGroup", actionGroup);
        }, this);
      } else {
        this.view.model.set("actionGroup", actionGroup);
      }
    },
    _getKbCampaignModelForTemplate: function (respTempId, responseCampaigns) {
      const templateCampaign = responseCampaigns.findWhere({
        respTempId: respTempId,
      });
      let kbCampaign;
      if (!templateCampaign) {
        kbCampaign = this.kbCampaignsWithDefault.findWhere({
          id: CampaignModel.campaignsWithDefault.DEFAULT_CAMPAIGN_ID,
        });
      } else {
        kbCampaign = this.kbCampaignsWithDefault.findWhere({
          id: templateCampaign.get("campaignId"),
        });
      }
      return kbCampaign;
    },
    setCampaigns: function () {
      if (!this.showCampaigns) {
        return;
      }
      this.conversationNode.onSynced(() => {
        const responseTemplates = this.conversationNode.responseTemplates();
        const responseCampaigns = this.conversationNode.responseCampaigns();
        responseTemplates.onSynced(() => {
          responseCampaigns.onSynced(() => {
            const responseTemplate = this.conversationNode.findResponseTemplate(
              this.selectedTouchpoint,
              this.selectedLanguage,
            );
            if (responseTemplate) {
              const kbCampaign = this._getKbCampaignModelForTemplate(
                responseTemplate.get("templateId"),
                responseCampaigns,
              );
              this.view.model.set("campaign", kbCampaign.get("name"));
              this.view.render();
            }
          });
        });
      });
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "action:group:changed",
        function () {
          this.setActionGroup();
        },
        this,
      );
      Messenger.vent.on(this.name + ":done:copy:responses", () => Messenger.save());
    },
    _setUpReqres: function () {
      //Reqres for getting the touchpoint and locale of the selected response template
      Messenger.reqres.setHandler(
        "selected:response:touchPointId",
        function () {
          if (!this.view) {
            return 0;
          }
          return this.view.model.get("selectedTouchpoint");
        },
        this,
      );
      Messenger.reqres.setHandler(
        "selected:response:localeId",
        function () {
          if (!this.view) {
            return 0;
          }
          return this.view.model.get("selectedLanguage");
        },
        this,
      );

      Messenger.reqres.setHandler(
        "fetch:conversation:response:attributes",
        function () {
          return this.conversationNode.topicAttributes();
        },
        this,
      );

      Messenger.reqres.setHandler(
        "fetch:conversation:response:categories",
        function () {
          return this.conversationNode.categories();
        },
        this,
      );
    },
    _showSubViews: function () {
      this._setShowDeleteCollabData();
      this._setIsResponseEmpty();
      if (this.responseTemplateStatus) {
        this.closeSubModules();
        this.responseTemplateStatus = null;
      }
      if (!this.isClosed && this.view.responseTemplateStatus) {
        const responseTemplate = this.view.model.get("responseTemplate");
        if (responseTemplate) {
          this.responseTemplateStatus = this.addSubModule(
            new ResponseTemplateStatusModule({
              region: this.view.responseTemplateStatus,
              allowDisable: this.allowDisable,
            }),
          );
          this.responseTemplateStatus.show(responseTemplate);
        }
      }
    },
    /*
     * Recursive function to find a conversation node by response ID
     * Returns null if no node is found
     */
    _findMatchingLiveNode: function (liveConversation, responseId) {
      if (liveConversation.get("responseId") === responseId) {
        return liveConversation;
      }

      for (let i = 0; i < liveConversation.decisionCol.length; i++) {
        const node = this._findMatchingLiveNode(liveConversation.decisionCol.at(i), responseId);
        if (node !== null) {
          return node;
        }
      }
      //otherwise return null
      return null;
    },
    _openResponseTranslationModal: function () {
      const topicId = Messenger.reqres.request("current:topic:id");
      //open up the response translation modal and pass it the response
      Messenger.vent.trigger(
        "show:modal",
        ResponseTranslationModalModule,
        {},
        {
          node: this.conversationNode,
          selectedTouchPoint: this.view.model.get("selectedTouchpoint"),
          selectedLanguage: this.view.model.get("selectedLanguage"),
        },
        {
          title: "topic.responseTranslation.title",
          closeButton: true,
          closeOnBackgroundClick: false,
          maximizeButton: true,
          modalHeight: "690px",
          modalWidth: "1500px",
          trackInteraction: {
            action: "Translate Response",
            id: [
              topicId,
              this.view.model.get("selectedTouchpoint"),
              this.view.model.get("selectedLanguage"),
            ],
            idType: ["Topic", "TouchPoint", "Source Locale"],
            object: "Topic Response",
          },
        },
      );
    },
    _openShareResponseLink: function () {
      const languageId = this.view.model.get("selectedLanguage");
      const topic = Messenger.reqres.request("current:topic");
      const touchpointId = this.view.model.get("selectedTouchpoint");
      const response = this.conversationNode.findResponseTemplate(touchpointId, languageId);
      const responseTemplateId = response.id;
      Messenger.vent.trigger(
        "show:modal",
        ShareResponseLinkModalModule,
        {},
        {
          languageId,
          responseTemplateId,
          topic,
          touchpointId,
        },
        {
          title: "topic.shareResponseLink.title",
          closeButton: true,
          closeOnBackgroundClick: false,
          trackInteraction: {
            action: "Share Response Link",
            id: [topic.id, touchpointId, languageId],
            idType: ["Topic", "TouchPoint", "Source Locale"],
            object: "Topic Response",
          },
        },
      );
    },
  });
});
